import { useEffect, useRef, useState } from 'react';
import type { LocalCaption } from '../lib/captions.js';

type FontSize = 'small' | 'medium' | 'large';
const FONT_SIZES: FontSize[] = ['small', 'medium', 'large'];
const FONT_CLASS: Record<FontSize, string> = {
  small: 'text-base',       // 16px
  medium: 'text-[22px]',    // 22px
  large: 'text-[32px]',     // 32px
};

const STORAGE_FONT = 'aslgo:captions:fontSize';

function loadFont(): FontSize {
  if (typeof window === 'undefined') return 'medium';
  const v = window.localStorage.getItem(STORAGE_FONT) as FontSize | null;
  return v && FONT_SIZES.includes(v) ? v : 'medium';
}

export interface CaptionsPanelProps {
  learner: LocalCaption[];
  guide: LocalCaption[];
  speaking: { learner: boolean; guide: boolean };
  micOff: { learner: boolean; guide: boolean };
  guideName: string | null;
  learnerName: string | null;
  /** Controlled CC on/off — shared with the in-call control bar. */
  enabled: boolean;
  onToggleEnabled: () => void;
  className?: string;
  /** Phone band: text only, one fixed two-line window per speaker, auto-pinned
   *  to the newest words. Sized by its own content — the parent does not
   *  provide the height. */
  compact?: boolean;
  'data-testid'?: string;
}

export function CaptionsPanel(props: CaptionsPanelProps) {
  const { enabled, onToggleEnabled } = props;
  const [fontSize, setFontSize] = useState<FontSize>(() => loadFont());

  useEffect(() => {
    window.localStorage.setItem(STORAGE_FONT, fontSize);
  }, [fontSize]);

  const idx = FONT_SIZES.indexOf(fontSize);
  const smaller = (): void => setFontSize(FONT_SIZES[Math.max(0, idx - 1)]);
  const larger = (): void => setFontSize(FONT_SIZES[Math.min(FONT_SIZES.length - 1, idx + 1)]);

  // High-contrast band in both places: an opaque row below the stage on a
  // phone, the full-width row under stage + chat rail on desktop.
  const surface = props.compact ? 'bg-black/85' : 'bg-[#0a090d]';

  return (
    <div
      data-testid={props['data-testid']}
      className={`${surface} text-white border-t border-white/10 px-4 sm:px-10 py-3 ${props.compact ? 'flex flex-col' : ''} ${props.className ?? ''}`}
    >
      {/* Controls are desktop-only: on a phone the band is text only and the
       *  control pill's Captions button is the toggle. */}
      {props.compact ? null : (
        <div className="flex items-center justify-between gap-3 mb-2 shrink-0">
          <span className="text-[0.65rem] uppercase tracking-[0.15em] text-ink-500">Live captions</span>
          <div className="flex items-center gap-2 text-[0.72rem]">
            <div className="inline-flex items-center rounded-md overflow-hidden border border-white/12">
              <button
                type="button"
                onClick={smaller}
                disabled={idx === 0}
                aria-label="Decrease caption size"
                className="px-2.5 py-1 bg-white/[0.06] hover:bg-white/15 disabled:opacity-40 text-ink-300"
              >
                A−
              </button>
              <button
                type="button"
                onClick={larger}
                disabled={idx === FONT_SIZES.length - 1}
                aria-label="Increase caption size"
                className="px-2.5 py-1 bg-white/[0.06] hover:bg-white/15 disabled:opacity-40 text-ink-300 border-l border-white/12"
              >
                A+
              </button>
            </div>
            <button
              type="button"
              onClick={onToggleEnabled}
              aria-label={enabled ? 'Hide captions' : 'Show captions'}
              className={`px-2.5 py-1 rounded-md border transition
                          ${enabled
                            ? 'border-status-successBright/30 bg-status-successBright/10 text-status-successBright'
                            : 'border-white/12 bg-white/[0.06] text-ink-300 hover:bg-white/15'}`}
            >
              {enabled ? 'CC ✓' : 'CC'}
            </button>
          </div>
        </div>
      )}

      {enabled ? (
        <div className={`${props.compact ? 'text-sm' : FONT_CLASS[fontSize]} space-y-2.5`}>
          <CaptionRow
            tone="guide"
            label={props.guideName ?? 'Guide'}
            captions={props.guide}
            micOff={props.micOff.guide}
            compact={props.compact}
          />
          <CaptionRow
            tone="learner"
            label={props.learnerName ?? 'Learner'}
            captions={props.learner}
            micOff={props.micOff.learner}
            compact={props.compact}
          />
        </div>
      ) : null}
    </div>
  );
}

const TONE_CHIP: Record<'guide' | 'learner', string> = {
  // Speaker colour-coding: guide = brand gold, learner = success green.
  guide:   'bg-brand-bright/16 text-brand-bright',
  learner: 'bg-status-successBright/16 text-status-successBright',
};

function CaptionRow({
  tone,
  label,
  captions,
  micOff,
  compact,
}: {
  tone: 'guide' | 'learner';
  label: string;
  captions: LocalCaption[];
  micOff: boolean;
  compact?: boolean;
}) {
  // Compact rows are a fixed two-line window (`.caption-window` in index.css —
  // it carries the `lh` fallback pair and the hidden overflow). Pin to the
  // bottom so the newest words stay visible; line-clamp would keep the FIRST
  // two lines instead.
  const windowRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!compact) return;
    const el = windowRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [compact, captions]);

  const chip = (
    <span className={`shrink-0 self-start rounded-md px-2 py-0.5 text-[0.68rem] font-semibold ${TONE_CHIP[tone]}`}>
      {label}
    </span>
  );

  // Every compact branch — mic-off, empty, and text — gets the SAME fixed
  // two-line box, so the band's height is constant for the whole call. Capping
  // only the text branch would just move the video's jump to the mic-off and
  // "…" transitions.
  const windowClass = compact ? 'block caption-window' : '';

  if (micOff) {
    return (
      <div className="flex gap-3 items-baseline">
        {chip}
        <span className={`text-white/55 italic leading-snug ${windowClass}`}>mic is off</span>
      </div>
    );
  }
  if (captions.length === 0) {
    return (
      <div className="flex gap-3 items-baseline">
        {chip}
        <span className={`text-white/40 leading-snug ${windowClass}`}>…</span>
      </div>
    );
  }
  return (
    <div className="flex gap-3 items-baseline">
      {chip}
      <span
        ref={windowRef}
        {...(compact ? { 'data-caption-window': '' } : {})}
        className={`leading-snug ${windowClass}`}
      >
        {captions.map((c) => (
          <span
            key={c.id}
            data-final={String(c.final)}
            className={c.final ? 'text-white' : 'text-white/60 italic'}
          >
            {c.text}{' '}
          </span>
        ))}
      </span>
    </div>
  );
}
